import React, { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  TextField,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import VisibilityIcon from "@mui/icons-material/Visibility"
import EditIcon from "@mui/icons-material/Edit"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"
import MainLayout from "../../layouts/MainLayout"
import SalesPage from "./SalesPage"
import { useNavigate } from "react-router-dom"
import { getSales } from "../../services/salesService"

const SHOP_ROLES = ["gerant_magasin", "gerant_depot_relais"]
const AGENT_ROLES = ["agent_commercial"]
const AGENT_VALIDATION_ROLES = [
  "platform_admin",
  "super_admin",
  "dg",
  "gestionnaire_operations",
]

const SALES_UI = {
  pageBg: "#f4f7fb",
  surface: "#ffffff",
  border: "#e2e8f0",
  soft: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
  primary: "#2563eb",
  success: "#16a34a",
  warning: "#ea580c",
  danger: "#dc2626",
}

function toNumber(value) {
  const number = Number(value || 0)
  return Number.isFinite(number) ? number : 0
}

function formatAmount(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatDate(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleDateString("fr-FR")
}

function getDateOnly(value) {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function isCurrentWeek(value) {
  const date = getDateOnly(value)

  if (!date) return false

  const today = new Date()
  const day = today.getDay() || 7
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - day + 1
  )
  const end = new Date(start)
  end.setDate(start.getDate() + 6)

  return date >= start && date <= end
}

function isCurrentMonth(value) {
  const date = getDateOnly(value)

  if (!date) return false

  const today = new Date()

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth()
  )
}

function isAgentOpportunity(sale) {
  return (
    sale?.created_by_role === "agent_commercial" ||
    sale?.order_source === "agent_commercial"
  )
}

function canDecideAgentOpportunity(sale) {
  const role = localStorage.getItem("kongosys_role") || ""

  return (
    AGENT_VALIDATION_ROLES.includes(role) &&
    isAgentOpportunity(sale) &&
    sale.status === "pending_price_validation" &&
    sale.payment_status === "unpaid"
  )
}

function canEditSale(sale) {
  const role = localStorage.getItem("kongosys_role") || ""

  if (canDecideAgentOpportunity(sale)) {
    return false
  }

  if (["platform_admin", "super_admin", "gestionnaire_operations"].includes(role)) {
    return sale.status !== "cancelled"
  }

  if (SHOP_ROLES.includes(role)) {
    return (
      sale.sale_type === "order" ||
      sale.status === "pending" ||
      sale.payment_status === "unpaid"
    )
  }

  return false
}

function getPaymentStatusLabel(value) {
  if (value === "paid") return "Payé"
  if (value === "partial") return "Partiel"
  if (value === "unpaid") return "Non payé"

  return value || "-"
}

function getPaymentStatusColor(value) {
  if (value === "paid") return "success"
  if (value === "partial") return "warning"
  if (value === "unpaid") return "error"

  return "default"
}

function getSaleStatusLabel(value, sale) {
  if (value === "pending_price_validation") return "Décision Entreprise principale"

  if (value === "paid_pending_delivery") return "Payé / attente livraison"

  if (value === "pending" && isAgentOpportunity(sale)) {
    if (sale?.payment_status === "paid") {
      return "Payé / attente livraison"
    }

    return "Prix décidé / attente paiement"
  }

  if (value === "pending") return "En attente"
  if (value === "confirmed") return "Vente confirmée"
  if (value === "cancelled") return "Refusée / annulée"

  return value || "-"
}

function getSaleStatusColor(value) {
  if (value === "confirmed") return "success"
  if (value === "cancelled") return "error"
  if (value === "pending_price_validation") return "warning"
  if (value === "paid_pending_delivery") return "warning"
  if (value === "pending") return "info"

  return "default"
}

function getSaleTypeLabel(sale) {
  if (isAgentOpportunity(sale)) {
    return "Demande agent"
  }

  if (sale.sale_type === "order") {
    return "Commande"
  }

  return "Vente"
}

function getSaleTypeColor(sale) {
  if (isAgentOpportunity(sale)) {
    return "info"
  }

  if (sale.sale_type === "order") {
    return "warning"
  }

  return "success"
}

function SummaryCard({
  title,
  value,
  subtitle,
  tone = "primary",
}) {
  const accentMap = {
    primary: SALES_UI.primary,
    success: SALES_UI.success,
    warning: SALES_UI.warning,
    danger: SALES_UI.danger,
    neutral: "#475569",
  }

  const accent = accentMap[tone] || SALES_UI.primary

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${SALES_UI.border}`,
        backgroundColor: SALES_UI.surface,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: accent,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.25 } }}>
        <Typography
          variant="caption"
          sx={{
            color: SALES_UI.muted,
            fontWeight: 800,
            letterSpacing: 0.35,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: SALES_UI.text,
            fontSize: { xs: 24, sm: 28 },
            lineHeight: 1.1,
            fontWeight: 900,
            mt: 0.7,
          }}
        >
          {value}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: SALES_UI.muted,
            mt: 0.7,
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  )
}

function SaleMobileCard({ sale, isAgentUser, navigate }) {
  const total = toNumber(sale.total_amount)
  const confirmedPaid = toNumber(sale.confirmed_paid_amount ?? sale.paid_amount)
  const paid = Math.min(total, confirmedPaid)
  const remaining = Math.max(0, total - confirmedPaid)
  const overpaid = Math.max(0, confirmedPaid - total)

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        mb: 1.5,
        border: `1px solid ${SALES_UI.border}`,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Box>
              <Typography fontWeight={900}>{sale.sale_code || "-"}</Typography>
              <Typography variant="body2" color="text.secondary">
                {sale.customer_name || "Client non renseigné"}
              </Typography>
            </Box>

            <Chip
              label={getSaleTypeLabel(sale)}
              color={getSaleTypeColor(sale)}
              size="small"
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={getPaymentStatusLabel(sale.payment_status)}
              color={getPaymentStatusColor(sale.payment_status)}
              size="small"
            />

            <Chip
              label={getSaleStatusLabel(sale.status, sale)}
              color={getSaleStatusColor(sale.status)}
              size="small"
            />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Montant
              </Typography>
              <Typography fontWeight={700}>{formatAmount(total)}</Typography>
            </Box>

            {!isAgentUser && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Encaissé
                  </Typography>
                  <Typography fontWeight={700}>{formatAmount(paid)}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reste
                  </Typography>
                  <Typography fontWeight={700}>
                    {formatAmount(remaining)}
                  </Typography>
                </Box>
              </>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography fontWeight={700}>{formatDate(sale.created_at)}</Typography>
            </Box>
          </Box>

          {!isAgentUser && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Point / créé par
              </Typography>
              <Typography variant="body2">
                {sale.store_name || "-"} • {sale.created_by_name || "-"}
              </Typography>
            </Box>
          )}

          {overpaid > 0 && (
            <Alert severity="warning" variant="outlined">
              Encaissement en trop : {formatAmount(overpaid)}
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant={canDecideAgentOpportunity(sale) ? "outlined" : "contained"}
              startIcon={<VisibilityIcon />}
              onClick={() => navigate(`/sales/${sale.id}`)}
            >
              Voir
            </Button>

            {canDecideAgentOpportunity(sale) && (
              <Button
                size="small"
                color="success"
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => navigate(`/sales/${sale.id}`)}
              >
                Décider
              </Button>
            )}

            {canEditSale(sale) && (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sales/${sale.id}/edit`)}
              >
                Modifier
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function SalesListPage() {
  const role = localStorage.getItem("kongosys_role") || ""
  const storeName = localStorage.getItem("kongosys_store_name") || ""
  const organizationName = localStorage.getItem("kongosys_organization_name") || ""

  const isShopUser = SHOP_ROLES.includes(role)
  const isAgentUser = AGENT_ROLES.includes(role)

  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(false)

  const [search, setSearch] = useState("")
  const [storeFilter, setStoreFilter] = useState("")
  const [customerFilter, setCustomerFilter] = useState("")
  const [agentFilter, setAgentFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  const navigate = useNavigate()
  const theme = useTheme()
  const fullScreenSaleDialog = useMediaQuery(theme.breakpoints.down("sm"))

  const [saleDialogOpen, setSaleDialogOpen] = useState(false)

  const openSaleDialog = () => {
    setSaleDialogOpen(true)
  }

  const closeSaleDialog = () => {
    setSaleDialogOpen(false)
  }

  const handleSaleSaved = async () => {
    setSaleDialogOpen(false)
    await loadSales()
  }

  useEffect(() => {
    loadSales()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }

  const loadSales = async () => {
    try {
      setLoading(true)

      const params = {}

      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo

      const data = await getSales(params)
      setSales(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Error loading sales:", err)
      showSnackbar(
        err?.response?.data?.detail || "Impossible de charger les ventes",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  const uniqueStores = useMemo(() => {
    const values = sales
      .map((sale) => sale.store_name)
      .filter((value) => value && String(value).trim() !== "")

    return [...new Set(values)].sort((a, b) => a.localeCompare(b))
  }, [sales])

  const uniqueCustomers = useMemo(() => {
    const values = sales
      .map((sale) => sale.customer_name)
      .filter((value) => value && String(value).trim() !== "")

    return [...new Set(values)].sort((a, b) => a.localeCompare(b))
  }, [sales])

  const uniqueAgents = useMemo(() => {
    const values = sales
      .map((sale) => sale.created_by_name)
      .filter((value) => value && String(value).trim() !== "")

    return [...new Set(values)].sort((a, b) => a.localeCompare(b))
  }, [sales])

  const filteredSales = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    return sales.filter((sale) => {
      const matchesSearch =
        keyword === "" ||
        String(sale.sale_code || "").toLowerCase().includes(keyword) ||
        String(sale.customer_name || "").toLowerCase().includes(keyword) ||
        String(sale.store_name || "").toLowerCase().includes(keyword) ||
        String(sale.created_by_name || "").toLowerCase().includes(keyword) ||
        String(sale.payment_status || "").toLowerCase().includes(keyword) ||
        String(sale.status || "").toLowerCase().includes(keyword)

      const matchesStore =
        storeFilter === "" || String(sale.store_name || "") === storeFilter

      const matchesCustomer =
        customerFilter === "" ||
        String(sale.customer_name || "") === customerFilter

      const matchesAgent =
        agentFilter === "" ||
        String(sale.created_by_name || "") === agentFilter

      const matchesStatus =
        statusFilter === "" || String(sale.status || "") === statusFilter

      return (
        matchesSearch &&
        matchesStore &&
        matchesCustomer &&
        matchesAgent &&
        matchesStatus
      )
    })
  }, [sales, search, storeFilter, customerFilter, agentFilter, statusFilter])

  const totals = useMemo(() => {
    return filteredSales.reduce(
      (acc, sale) => {
        const total = toNumber(sale.total_amount)
        const confirmedPaid = toNumber(
          sale.confirmed_paid_amount ?? sale.paid_amount
        )

        const paid = Math.min(total, confirmedPaid)
        const remaining = Math.max(0, total - confirmedPaid)
        const overpaid = Math.max(0, confirmedPaid - total)

        acc.transactions += 1
        acc.total += total
        acc.paid += paid
        acc.remaining += remaining
        acc.overpaid += overpaid

        if (isAgentOpportunity(sale)) {
          acc.agentOrders += 1
        } else if (sale.sale_type === "order") {
          acc.orders += 1
        } else if (sale.sale_type === "direct") {
          acc.directSales += 1
        }

        if (sale.status === "pending_price_validation") {
          acc.pendingDecision += 1
        }

        if (
          sale.status === "pending" &&
          isAgentOpportunity(sale) &&
          sale.payment_status !== "paid"
        ) {
          acc.priceDecided += 1
        }

        if (
          isAgentOpportunity(sale) &&
          (sale.status === "paid_pending_delivery" ||
            (sale.status === "pending" && sale.payment_status === "paid"))
        ) {
          acc.paidPendingDelivery += 1
        }

        if (sale.status === "confirmed") {
          acc.confirmed += 1
        }

        if (isAgentOpportunity(sale) && sale.status === "confirmed") {
          acc.agentDelivered += 1
        }

        if (sale.payment_status === "paid") {
          acc.paidSales += 1
        }

        if (isCurrentWeek(sale.created_at)) {
          acc.weekCount += 1
          acc.weekAmount += total
        }

        if (isCurrentMonth(sale.created_at)) {
          acc.monthCount += 1
          acc.monthAmount += total
        }

        if (sale.customer_name) {
          acc.customers.add(sale.customer_name)
        }

        return acc
      },
      {
        transactions: 0,
        total: 0,
        paid: 0,
        remaining: 0,
        overpaid: 0,
        orders: 0,
        agentOrders: 0,
        directSales: 0,
        pendingDecision: 0,
        priceDecided: 0,
        paidPendingDelivery: 0,
        confirmed: 0,
        agentDelivered: 0,
        paidSales: 0,
        weekCount: 0,
        weekAmount: 0,
        monthCount: 0,
        monthAmount: 0,
        customers: new Set(),
      }
    )
  }, [filteredSales])

  const resetFilters = () => {
    setSearch("")
    setStoreFilter("")
    setCustomerFilter("")
    setAgentFilter("")
    setStatusFilter("")
    setDateFrom("")
    setDateTo("")
  }

  const cardColumns = isAgentUser
    ? {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        lg: "repeat(4, 1fr)",
      }
    : {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        lg: "repeat(4, 1fr)",
      }

  return (
    <MainLayout>
      <Box
        sx={{
          maxWidth: 1540,
          width: "100%",
          mx: "auto",
          pb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                color: SALES_UI.primary,
                fontWeight: 900,
                letterSpacing: 1,
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              VENTES
            </Typography>

            <Typography
              sx={{
                color: SALES_UI.text,
                fontSize: { xs: 26, sm: 30, md: 34 },
                lineHeight: 1.08,
                fontWeight: 900,
                mb: 0.6,
              }}
            >
              {isAgentUser
                ? "Mes demandes commerciales"
                : "Demandes commerciales & ventes"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {isAgentUser
                ? "Suivi des clients amenés à Entreprise principale, décisions de prix, paiements et confirmations."
                : isShopUser
                ? `${organizationName || "Organisation"} • ${
                    storeName || "Votre shop"
                  }`
                : "Vue globale des ventes, commandes shops et demandes des agents commerciaux."}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openSaleDialog}
            sx={{
              alignSelf: { xs: "stretch", md: "center" },
              minHeight: 44,
              px: 2.25,
              borderRadius: 2.5,
              fontWeight: 800,
              boxShadow: "0 8px 18px rgba(37, 99, 235, 0.20)",
            }}
          >
            {isAgentUser ? "Nouvelle demande" : "Nouvelle vente"}
          </Button>
        </Box>

        {isAgentUser && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Vous amenez les clients à Entreprise principale. Le montant affiché ici est le
            montant système. Entreprise principale décide ensuite le prix final, puis le
            client paie.
          </Alert>
        )}

        {!isAgentUser && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            Les lignes <strong>Décision Entreprise principale</strong> ne sont pas encore
            des ventes réelles. Ouvrez le détail pour confirmer le prix final ou
            refuser la demande.
          </Alert>
        )}

        {totals.overpaid > 0 && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            Une anomalie d'encaissement existe :{" "}
            <strong>{formatAmount(totals.overpaid)}</strong> encaissé en trop.
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: cardColumns,
            gap: 2,
            mb: 2,
          }}
        >
          {isAgentUser ? (
            <>
              <SummaryCard
                title="Demandes créées"
                value={totals.transactions}
                subtitle="Clients amenés"
                tone="primary"
              />
              <SummaryCard
                title="Montant système"
                value={formatAmount(totals.total)}
                subtitle="Avant décision Entreprise principale"
                tone="neutral"
              />
              <SummaryCard
                title="Décision en attente"
                value={totals.pendingDecision}
                subtitle="Prix à décider"
                tone="warning"
              />
              <SummaryCard
                title="Clients touchés"
                value={totals.customers.size}
                subtitle="Clients liés à vos demandes"
              />
              <SummaryCard
                title="Cette semaine"
                value={totals.weekCount}
                subtitle={`${formatAmount(totals.weekAmount)} système`}
              />
              <SummaryCard
                title="Ce mois"
                value={totals.monthCount}
                subtitle={`${formatAmount(totals.monthAmount)} système`}
              />
              <SummaryCard
                title="Prix décidés"
                value={totals.priceDecided}
                subtitle="En attente paiement client"
              />
              <SummaryCard
                title="Payées à livrer"
                value={totals.paidPendingDelivery}
                subtitle="Sortie stock à confirmer"
              />
              <SummaryCard
                title="Livrées"
                value={totals.agentDelivered}
                subtitle="Base future commission"
              />
            </>
          ) : (
            <>
              <SummaryCard
                title="Chiffre d'affaires"
                value={formatAmount(totals.total)}
                subtitle="Ventes + commandes + demandes"
                tone="primary"
              />
              <SummaryCard
                title="Encaissements"
                value={formatAmount(totals.paid)}
                subtitle="Paiements confirmés"
                tone="success"
              />
              <SummaryCard
                title="Demandes agents"
                value={totals.agentOrders}
                subtitle="Clients amenés par agents"
                tone="neutral"
              />
              <SummaryCard
                title="À décider"
                value={totals.pendingDecision}
                subtitle="Prix à décider Entreprise principale"
                tone="warning"
              />
              <SummaryCard
                title="Payées à livrer"
                value={totals.paidPendingDelivery}
                subtitle="Demandes agents après paiement"
                tone="success"
              />
            </>
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 2,
            borderRadius: 3,
            border: `1px solid ${SALES_UI.border}`,
            backgroundColor: SALES_UI.surface,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(250px, 2fr) repeat(5, minmax(145px, 1fr)) auto auto",
              },
              gap: 2,
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Recherche"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <TextField
              size="small"
              label="Du"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />

            <TextField
              size="small"
              label="Au"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />

            {!isShopUser && !isAgentUser && (
              <TextField
                size="small"
                select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
              >
                <MenuItem value="">Tous les points</MenuItem>
                {uniqueStores.map((store) => (
                  <MenuItem key={store} value={store}>
                    {store}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              size="small"
              select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <MenuItem value="">Tous les clients</MenuItem>
              {uniqueCustomers.map((customer) => (
                <MenuItem key={customer} value={customer}>
                  {customer}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Tous les statuts</MenuItem>
              <MenuItem value="pending_price_validation">
                Décision Entreprise principale
              </MenuItem>
              <MenuItem value="pending">Prix décidé / attente paiement</MenuItem>
              <MenuItem value="paid_pending_delivery">Payé / attente livraison</MenuItem>
              <MenuItem value="confirmed">Vente confirmée</MenuItem>
              <MenuItem value="cancelled">Refusée / annulée</MenuItem>
            </TextField>

            {!isAgentUser && (
              <TextField
                size="small"
                select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
              >
                <MenuItem value="">Tous les agents/utilisateurs</MenuItem>
                {uniqueAgents.map((agent) => (
                  <MenuItem key={agent} value={agent}>
                    {agent}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Button variant="contained" onClick={loadSales}>
              Charger
            </Button>

            <Button variant="outlined" onClick={resetFilters}>
              Réinitialiser
            </Button>
          </Box>
        </Paper>

        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {loading ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${SALES_UI.border}`,
              }}
            >
              <Typography color="text.secondary">
                Chargement des données...
              </Typography>
            </Paper>
          ) : filteredSales.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${SALES_UI.border}`,
              }}
            >
              <Typography color="text.secondary">Aucune donnée trouvée</Typography>
            </Paper>
          ) : (
            filteredSales.map((sale) => (
              <SaleMobileCard
                key={sale.id}
                sale={sale}
                isAgentUser={isAgentUser}
                navigate={navigate}
              />
            ))
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            display: { xs: "none", md: "block" },
            border: `1px solid ${SALES_UI.border}`,
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ minWidth: isAgentUser ? 980 : 1280 }}>
              <TableHead sx={{ backgroundColor: SALES_UI.soft }}>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Client</TableCell>
                  {!isAgentUser && <TableCell>Point / Shop</TableCell>}
                  {!isAgentUser && <TableCell>Créé par</TableCell>}
                  <TableCell>
                    {isAgentUser ? "Montant système" : "Total"}
                  </TableCell>
                  {!isAgentUser && <TableCell>Encaissé</TableCell>}
                  {!isAgentUser && <TableCell>Reste</TableCell>}
                  {!isAgentUser && <TableCell>Anomalie</TableCell>}
                  <TableCell>Paiement</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isAgentUser ? 8 : 13}>
                      <Typography color="text.secondary">
                        Chargement des données...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredSales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAgentUser ? 8 : 13}>
                      <Typography color="text.secondary">
                        Aucune donnée trouvée
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSales.map((sale) => {
                    const total = toNumber(sale.total_amount)
                    const confirmedPaid = toNumber(
                      sale.confirmed_paid_amount ?? sale.paid_amount
                    )
                    const paid = Math.min(total, confirmedPaid)
                    const remaining = Math.max(0, total - confirmedPaid)
                    const overpaid = Math.max(0, confirmedPaid - total)

                    return (
                      <TableRow key={sale.id} hover>
                        <TableCell>{sale.sale_code || "-"}</TableCell>

                        <TableCell>
                          <Chip
                            label={getSaleTypeLabel(sale)}
                            size="small"
                            color={getSaleTypeColor(sale)}
                          />
                        </TableCell>

                        <TableCell>{sale.customer_name || "-"}</TableCell>

                        {!isAgentUser && (
                          <TableCell>{sale.store_name || "-"}</TableCell>
                        )}

                        {!isAgentUser && (
                          <TableCell>{sale.created_by_name || "-"}</TableCell>
                        )}

                        <TableCell>{formatAmount(total)}</TableCell>

                        {!isAgentUser && (
                          <TableCell>{formatAmount(paid)}</TableCell>
                        )}

                        {!isAgentUser && (
                          <TableCell>{formatAmount(remaining)}</TableCell>
                        )}

                        {!isAgentUser && (
                          <TableCell>
                            {overpaid > 0 ? (
                              <Chip
                                label={`+${formatAmount(overpaid)}`}
                                color="error"
                                size="small"
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        )}

                        <TableCell>
                          <Chip
                            label={getPaymentStatusLabel(sale.payment_status)}
                            color={getPaymentStatusColor(sale.payment_status)}
                            size="small"
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={getSaleStatusLabel(sale.status, sale)}
                            size="small"
                            color={getSaleStatusColor(sale.status)}
                          />
                        </TableCell>

                        <TableCell>{formatDate(sale.created_at)}</TableCell>

                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              variant={
                                canDecideAgentOpportunity(sale)
                                  ? "outlined"
                                  : "contained"
                              }
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => navigate(`/sales/${sale.id}`)}
                            >
                              Voir
                            </Button>

                            {canDecideAgentOpportunity(sale) && (
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => navigate(`/sales/${sale.id}`)}
                              >
                                Décider
                              </Button>
                            )}

                            {canEditSale(sale) && (
                              <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/sales/${sale.id}/edit`)}
                              >
                                Modifier
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={saleDialogOpen}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") {
            closeSaleDialog()
          }
        }}
        fullScreen={fullScreenSaleDialog}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: "94vw", md: "88vw" },
            maxWidth: { md: 1120, lg: 1180 },
            height: { xs: "100%", sm: "92vh" },
            maxHeight: { xs: "100%", sm: "92vh" },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 3.5 },
            overflow: "hidden",
            boxShadow: "0 28px 80px rgba(15, 23, 42, 0.28)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderBottom: `1px solid ${SALES_UI.border}`,
            backgroundColor: SALES_UI.surface,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: SALES_UI.primary,
                  fontWeight: 900,
                  letterSpacing: 0.8,
                  lineHeight: 1.1,
                }}
              >
                VENTES
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 19, sm: 22 },
                  fontWeight: 900,
                  color: SALES_UI.text,
                  lineHeight: 1.2,
                }}
              >
                {isAgentUser ? "Nouvelle demande commerciale" : "Nouvelle vente / commande"}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: SALES_UI.muted,
                  display: { xs: "none", sm: "block" },
                  mt: 0.25,
                }}
              >
                Saisissez l'opération sans quitter l'historique des ventes.
              </Typography>
            </Box>

            <IconButton
              aria-label="Fermer"
              onClick={closeSaleDialog}
              sx={{
                flexShrink: 0,
                border: `1px solid ${SALES_UI.border}`,
                backgroundColor: SALES_UI.soft,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers={false}
          sx={{
            p: { xs: 1, sm: 1.5, md: 2 },
            backgroundColor: SALES_UI.pageBg,
            overflowY: "auto",
          }}
        >
          <SalesPage
            embedded
            onClose={closeSaleDialog}
            onSaved={handleSaleSaved}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}